import { UserRequester } from '@cobliteam/api-lib';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import { Environment } from '@cobliteam/react-environments/src/Environments';
import Alert from '@cobliteam/ui/components/Alert';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Input from '@cobliteam/ui/form/Input';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useMemo, useState } from 'react';
import DevToolsPanel from '../DevToolsPanel';

const REQUIRED_PERMISSIONS = ['create-update-delete:users-and-invites', 'cobler:admin', 'basic:permission'];

const PermissionsData = () => {
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const { getUser, useDefaultRoles } = useUser();
  const user = getUser();
  const { data: defaultRoles } = useDefaultRoles();

  const [selectedUserPermissions, setSelectedUserPermissions] = useState<string[]>(user.grantedPermissions);

  // Was constructed at module scope, so importing this file hit Environment and
  // built a requester even when the dev tools were gated off entirely.
  const userRequester = useMemo(() => new UserRequester({ host: Environment.getInstance().getEnvironment().herbieApiHost }), []);

  const allPermissions = useMemo(() => new Set<string>((defaultRoles || []).flatMap((role) => role.permissions)), [defaultRoles]);

  const groupedPermissions = useMemo(() => {
    const term = search.trim().toLowerCase();

    // Each entry carries its full token rather than having the render rebuild
    // `${type}:${name}`: a permission with no `:` has no name half, and the rebuild
    // produced `<permission>:undefined` — a checkbox bound to a token that doesn't
    // exist, so ticking it saved garbage.
    const groups = Array.from(allPermissions).reduce<Record<string, Array<{ name: string; permission: string }>>>((acc, permission) => {
      if (term && !permission.toLowerCase().includes(term)) return acc;

      const separatorIndex = permission.indexOf(':');
      const type = separatorIndex === -1 ? 'outros' : permission.slice(0, separatorIndex);
      const name = separatorIndex === -1 ? permission : permission.slice(separatorIndex + 1);

      const group = acc[type] ?? [];
      group.push({ name, permission });
      acc[type] = group;

      return acc;
    }, {});

    // Sorted once here, not on every reduce iteration.
    return Object.entries(groups)
      .map(([type, items]) => [type, items.sort((a, b) => a.name.localeCompare(b.name))] as const)
      .sort(([typeA], [typeB]) => typeA.localeCompare(typeB));
  }, [allPermissions, search]);

  // "Selecionar todas" sits beside the search box, so it acts on what the filter
  // left visible. Operating on the whole catalog silently toggled rows the user
  // couldn't see.
  const togglableVisiblePermissions = useMemo(
    () => groupedPermissions.flatMap(([, items]) => items.map((item) => item.permission)).filter((permission) => !REQUIRED_PERMISSIONS.includes(permission)),
    [groupedPermissions]
  );

  const areAllSelected = togglableVisiblePermissions.length > 0 && togglableVisiblePermissions.every((permission) => selectedUserPermissions.includes(permission));

  const handlePermissionChange = (permission: string) => {
    setSelectedUserPermissions((previous) => (previous.includes(permission) ? previous.filter((p) => p !== permission) : [...previous, permission]));
  };

  const handleSelectAll = () => {
    setSelectedUserPermissions((previous) =>
      areAllSelected ? previous.filter((permission) => !togglableVisiblePermissions.includes(permission)) : [...new Set([...previous, ...togglableVisiblePermissions])]
    );
  };

  const handleSave = () => {
    setLoading(true);
    userRequester
      // Deduped: a user who already holds a required permission would otherwise
      // send it twice.
      .saveUserPermissions(user.id, user.role, [], Array.from(new Set([...selectedUserPermissions, ...REQUIRED_PERMISSIONS])))
      .then(() => window.location.reload())
      // Previously `.catch(() => {})`: a failed save looked exactly like a
      // successful one, minus the reload.
      .catch(() => Alert.error('Não foi possível salvar as permissões.'))
      .finally(() => setLoading(false));
  };

  return (
    <DevToolsPanel
      title='Permissões'
      description={user.role === 'cobler' ? 'Cobler já tem permissões especiais — alterar aqui recarrega a página.' : 'Salvar recarrega a página para reaplicar as permissões.'}
      actions={
        <Button loading={loading} disabled={loading} onClick={handleSave}>
          Salvar
        </Button>
      }
      toolbar={
        <>
          <Input
            size='sm'
            aria-label='Filtrar permissões'
            placeholder='Filtrar permissões...'
            startIcon={<Icon name='search' size='sm' />}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <div className='flex shrink-0 items-center gap-2'>
            <Checkbox id='select-all' checked={areAllSelected} onCheckedChange={handleSelectAll} />
            <Label htmlFor='select-all' className='whitespace-nowrap'>
              Selecionar todas ({selectedUserPermissions.length})
            </Label>
          </div>
        </>
      }
    >
      {groupedPermissions.length === 0 ? (
        <Typography variant='body' color='secondary'>
          Nenhuma permissão corresponde a “{search}”.
        </Typography>
      ) : (
        // Multi-column packing, not one full-width row per group: 10 of the 14
        // groups hold a single permission, so stacking them wasted ~75% of the
        // width and 1200px of scroll. `break-inside-avoid` keeps a group whole,
        // and the browser balances the uneven sizes (view=9, basic=1).
        <div className='columns-1 laptop:columns-3 mobile:columns-2 gap-x-8'>
          {groupedPermissions.map(([type, items]) => {
            const selectedInGroup = items.filter((item) => selectedUserPermissions.includes(item.permission)).length;

            return (
              <div key={type} className='mb-4 break-inside-avoid'>
                {/* The group label is the taxonomy prefix, not the subject: it reads
                    quieter than the permission it contains, where a `heading-2` had
                    it shouting over the thing you actually click. */}
                <div className='mb-1 flex items-baseline justify-between gap-2 border-gray-4 border-b pb-1'>
                  <Typography variant='caption-xs' className='truncate font-medium text-gray-11'>
                    {type}
                  </Typography>
                  <Typography variant='caption-xs' color='secondary' className='shrink-0 tabular-nums'>
                    {selectedInGroup}/{items.length}
                  </Typography>
                </div>

                <div className='flex flex-col'>
                  {items.map(({ name, permission }) => {
                    const isRequired = REQUIRED_PERMISSIONS.includes(permission);

                    return (
                      // `title` exposes the full token — it's what you grep for, and
                      // splitting it across label and heading lost it entirely.
                      <div key={permission} className='flex items-center gap-2 py-1' title={permission}>
                        <Checkbox
                          id={permission}
                          size='sm'
                          disabled={isRequired}
                          checked={selectedUserPermissions.includes(permission)}
                          onCheckedChange={() => handlePermissionChange(permission)}
                        />
                        <Label htmlFor={permission} className='min-w-0 truncate text-gray-12'>
                          {name}
                        </Label>
                        {/* Text, not just dimming: a locked permission was signalled
                            by opacity alone, which colour-alone rules out. */}
                        {isRequired && <span className='shrink-0 rounded bg-gray-3 px-1 py-px text-[10px] text-gray-11'>fixa</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DevToolsPanel>
  );
};

export default PermissionsData;
